import { useEffect, useState } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useAppTheme } from '@/context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card_Widget';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, PieChart } from '@/components/charts';
import { charity } from '@/services/review.service';
import type { CharityReviewStats } from '@/services/review.service';
import { mockCharityReviewStats } from '@/utils/mockData';

export default function RatingsDashboardCharity() {
  const { user } = useAuthV2();
  const navigate = useNavigate();
  const { themeConfig } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<CharityReviewStats | null>(null);
  const [processedStats, setProcessedStats] = useState({
    positiveReviews: 0,
    negativeReviews: 0,
    categoryRatings: {
      'Timeliness': 0,
      'Politeness': 0,
      'Overall Satisfaction': 0
    }
  });

  useEffect(() => {
    const fetchReviewStats = async () => {
      // Get current user with normalizeUser
      const { createClient, normalizeUser } = await import('@/services/supabase');
      const supabase = await createClient();
      const { data: { session } } = await supabase.auth.getSession();
      
      // Check if we have a valid session
      if (!session?.user) {
        console.error('No valid Supabase session found');
        setLoading(false);
        setError('Authentication error. Please log in again.');
        return;
      }
      
      // Use normalizeUser to get consistent user data
      const normalizedUser = normalizeUser(session.user);
      const isSuperAdmin = normalizedUser.role === 'super_admin';
      
      // For charity users, business_id is used as charity_id in the normalized user
      const charityId = normalizedUser.business_id;
      
      // Default charity ID for super admin if none is found
      const effectiveCharityId = isSuperAdmin ? '9d4f08e8-f9b0-4cd8-a55a-49435583f101' : charityId;
      
      if (!effectiveCharityId) {
        setError('No charity ID found');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        let reviewStats: CharityReviewStats;
        
        if (isSuperAdmin) {
          // For super admin, always use mock data
          console.log('Using mock data for super admin');
          
          // Get charity mock data by ID or use the default one
          reviewStats = mockCharityReviewStats[effectiveCharityId];
          if (!reviewStats) {
            console.log('Using default mock charity review stats');
            reviewStats = mockCharityReviewStats['9d4f08e8-f9b0-4cd8-a55a-49435583f101'];
          }
          
          // Short timeout to simulate API call
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          // For regular charity users, use real API with proper error handling
          try {
            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Request timed out after 5000ms')), 5000);
            });
            
            // Race between the actual API call and the timeout
            reviewStats = await Promise.race([
              charity.reviews.getStats(effectiveCharityId),
              timeoutPromise
            ]) as CharityReviewStats;
            
            // If we got an empty response or no reviews, throw an error
            if (!reviewStats || reviewStats.total_reviews === 0) {
              throw new Error('No reviews found for this charity');
            }
          } catch (apiError) {
            console.error('Error fetching charity reviews:', apiError);
            throw apiError instanceof Error 
              ? apiError 
              : new Error('Failed to load review data');
          }
        }
        
        setStats(reviewStats);
        
        // Process the data for display
        const positive = reviewStats.total_reviews > 0 
          ? Math.round((reviewStats.rating_distribution['4'] + reviewStats.rating_distribution['5']) || 0)
          : 0;
        const negative = reviewStats.total_reviews > 0
          ? Math.round((reviewStats.rating_distribution['1'] + reviewStats.rating_distribution['2'] + reviewStats.rating_distribution['3']) || 0) 
          : 0;
          
        setProcessedStats({
          positiveReviews: positive,
          negativeReviews: negative,
          categoryRatings: {
            'Timeliness': reviewStats.timeliness_percent,
            'Politeness': reviewStats.politeness_percent,
            'Overall Satisfaction': reviewStats.overall_satisfaction_percent
          }
        });
      } catch (err) {
        console.error('Error fetching review stats:', err);
        const errorMessage = err instanceof Error && err.message.includes('timed out')
          ? 'Request timed out. Please try again.'
          : 'Failed to load review statistics';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchReviewStats();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center mb-6">
            <button
              type="button"
              onClick={() => navigate('/charity/dashboard')}
              className="mr-4 text-gray-500 hover:text-blue-700"
            >
            
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Ratings Dashboard</h1>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => navigate('/charity/dashboard')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center mb-6">
            <button
              type="button"
              onClick={() => navigate('/charity/dashboard')}
              className="mr-4 text-gray-500 hover:text-blue-700"
            >
            
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Ratings Dashboard</h1>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <p className="text-gray-600 mb-4">No ratings data available for this charity</p>
            <button
              onClick={() => navigate('/charity/dashboard')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate('/charity/dashboard')}
              className="mr-4 text-gray-500 hover:text-navy-blue"
            >
              
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Ratings Dashboard</h1>
          </div>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="distribution">Distribution</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Average Rating</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.average_rating.toFixed(1)}</div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Total Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.total_reviews}</div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Positive Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{processedStats.positiveReviews}</div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Negative Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{processedStats.negativeReviews}</div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="distribution" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Rating Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart
                    data={[
                      { name: '5 Stars', value: stats.rating_distribution['5'] || 0 },
                      { name: '4 Stars', value: stats.rating_distribution['4'] || 0 },
                      { name: '3 Stars', value: stats.rating_distribution['3'] || 0 },
                      { name: '2 Stars', value: stats.rating_distribution['2'] || 0 },
                      { name: '1 Star', value: stats.rating_distribution['1'] || 0 }
                    ]}
                  />
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Review Sentiment</CardTitle>
                </CardHeader>
                <CardContent>
                  <PieChart
                    data={[
                      { name: 'Positive', value: processedStats.positiveReviews },
                      { name: 'Negative', value: processedStats.negativeReviews }
                    ]}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <Card className="bg-white shadow-md">
              <CardHeader>
                <CardTitle style={{ color: themeConfig.primary }}>Service Metrics (%)</CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart
                  data={[
                    { name: 'Timeliness', value: processedStats.categoryRatings['Timeliness'] },
                    { name: 'Politeness', value: processedStats.categoryRatings['Politeness'] },
                    { name: 'Overall Satisfaction', value: processedStats.categoryRatings['Overall Satisfaction'] }
                  ]}
                />
              </CardContent>
            </Card>
            {stats.recent_comments.length > 0 && (
              <Card className="bg-white shadow-md mt-6">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Recent Comments</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {stats.recent_comments.map((comment, index) => (
                      <li key={index} className="border-b pb-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 mr-3">
                            <div className="bg-green-100 text-green-700 font-bold rounded-full w-10 h-10 flex items-center justify-center">
                              {comment.rating}
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-700">{comment.comment}</p>
                            <p className="text-xs text-gray-500 mt-1">{new Date(comment.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
} 